<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <div class="panel-title-group">
        <h2 class="panel-title">
          Time saved by automations
        </h2>
        <p class="panel-subtitle">
          Calculated by taking the 'Time saved' setting on the automation and multiplying it by the number of times the automation has been enrolled
        </p>
      </div>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <table
        v-else-if="enrollments.length > 0"
        class="panel-table"
      >
        <thead>
          <tr>
            <th>Automation</th>
            <th class="text-right">
              Time Saved
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(enrollment, index) in enrollments"
            :key="index"
            :class="{ 'clickable': enrollment.workflowId }"
            @click="enrollment.workflowId ? openWorkflowGuarded(enrollment.workflowId) : null"
          >
            <td>
              <span class="enrollment-name-cell">
                <span class="enrollment-name-text">{{ enrollment.name }}</span>
                <span
                  v-if="enrollment.isDefault"
                  class="default-chip"
                >Default</span>
              </span>
            </td>
            <td class="text-right">
              {{ formatMinutes(enrollment.minutesSaved) }}
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-else
        class="panel-empty"
      >
        No automation data available
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import { useTimeSavedData } from '../composables/useWidgetData.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

defineEmits(['close'])

const { data, loading, error, cleanup } = useTimeSavedData({ priority: 1 })

const enrollments = computed(() => {
  if (!data.value?.enrollments) return []
  return data.value.enrollments
})

const formatMinutes = (minutes) => {
  if (!minutes) return '0 min'
  if (minutes >= 60) {
    const hours = minutes / 60
    const formatted = hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)
    return `${formatted} hour${formatted === '1' ? '' : 's'}`
  }
  return `${minutes} min`
}

const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''
  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (!clientId || !canvasUUID) {
    try {
      if (document.referrer) {
        const referrerParams = new URLSearchParams(new URL(document.referrer).search)
        if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
    } catch (e) { /* ignore */ }
  }
  return { clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const openWorkflowGuarded = guard((workflowId) => {
  if (!workflowId) return
  const params = getUrlParams()
  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }
  const finalCanvasUUID = params.canvasUUID || generateCanvasUUID()
  const directMessage = {
    workflowId: workflowId.toString(),
    canvasUUID: finalCanvasUUID,
    client_id: params.clientId || undefined
  }
  const commandMessage = { command: 'WorkflowView', payload: directMessage }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(directMessage, '*')
      setTimeout(() => window.parent.postMessage(commandMessage, '*'), 50)
    }
  } catch (err) {
    console.error('Error posting WorkflowView message:', err)
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title-group {
  flex: 1;
  min-width: 0;
}

.panel-title {
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  margin: 4px 0 0;
  line-height: 1.4;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.panel-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.panel-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.panel-table th.text-right {
  text-align: right;
  width: 90px;
}

.panel-table tbody tr {
  height: 48px;
}

.panel-table td {
  padding: 0 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-table td:first-child {
  overflow: hidden;
}

.enrollment-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.enrollment-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.default-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.panel-table td.text-right {
  text-align: right;
}

.panel-table tbody tr:last-child td {
  border-bottom: none;
}

.panel-table tbody tr.clickable {
  cursor: pointer;
}

.panel-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
